import {
  Badge,
  Button,
  ConstraintChip,
  ContestTimer,
  FeedbackBanner,
  MasteryHeatmap,
  ReadinessMeter,
  Tag,
} from '@usacow/ds';

/** A faux app-window frame so each panel reads as a real screen crop. */
function Frame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="show-frame">
      <div className="show-frame__bar">
        <span className="show-frame__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="show-frame__label">{label}</span>
      </div>
      <div className="show-frame__body">{children}</div>
    </div>
  );
}

const HEATMAP = Array.from({ length: 30 }, (_, i) => {
  const base = [0.9, 0.84, 0.3, 0.7, 0.15, 0.55, 0.88, 0.42, 0.66, 0.5][i % 10]!;
  return { mastery: Math.min(1, base * (0.6 + ((i * 13) % 9) / 12)), flag: [4, 17].includes(i) };
});

/** Product showcase — the actual app surfaces, built from the real DS components (so
 *  they're pixel-faithful and follow the light/dark toggle), arranged as screen crops. */
export function Showcase() {
  return (
    <section className="mkt-section mkt-section--alt" id="product">
      <h2 className="mkt-section__h">See the actual app</h2>
      <p className="show-sub">
        Not mockups — these are the real product surfaces. Try the light/dark toggle up top.
      </p>

      <div className="show-grid">
        <Frame label="Home · cockpit">
          <div className="t-eyebrow">recommended now · 22 min</div>
          <div className="show-title">Repair: graph modeling under disguised constraints</div>
          <p className="show-why">
            Three of your last five Gold attempts modeled a grid as a flat array when the rules made
            it a weighted graph.
          </p>
          <Button variant="primary" size="sm">
            Start repair session
          </Button>
          <div className="show-divider" />
          <div className="show-row">
            <span className="t-eyebrow">contest readiness</span>
            <Badge tone="gold" dot>
              Gold
            </Badge>
          </div>
          <ReadinessMeter
            division="gold"
            score={74}
            threshold={80}
            delta={3}
            style={{ width: '100%' }}
          />
        </Frame>

        <Frame label="Drill · pattern discriminator">
          <div className="t-eyebrow">near-miss pair</div>
          <div className="show-q">Which needs a different method than a fixed sliding window?</div>
          <div className="show-opts">
            <div className="show-opt">
              <span className="show-opt__k">A</span>Max sum subarray of length exactly K
            </div>
            <div className="show-opt is-on">
              <span className="show-opt__k">B</span>Max sum subarray of length at most K
            </div>
          </div>
          <FeedbackBanner
            state="correct"
            title="Correct."
            slug="constraint: exactly → at most"
            style={{ marginTop: 12 }}
          >
            “At most K” breaks the fixed-window invariant — you need a deque, not one sliding sum.
          </FeedbackBanner>
        </Frame>

        <Frame label="Guided problem · constraints">
          <div className="show-row" style={{ marginBottom: 10 }}>
            <Tag>graph</Tag>
            <Tag>shortest path</Tag>
          </div>
          <div className="show-chips">
            <ConstraintChip label="N" value="≤ 1000" note="grid 10⁶ cells" />
            <ConstraintChip label="K" value="≤ 10" />
            <ConstraintChip value="O(N²·K·log)" variant="derived" note="layered Dijkstra" />
          </div>
          <FeedbackBanner
            state="partial"
            title="Close."
            slug="objective + relation tagged"
            style={{ marginTop: 12 }}
          >
            “length” is an edge weight — tag it as an entity. Your layered-graph model is accepted.
          </FeedbackBanner>
        </Frame>

        <Frame label="Contest · timed sim">
          <div className="show-row">
            <Badge tone="gold" dot>
              Gold sim
            </Badge>
            <ContestTimer seconds={2228} warnAt={900} critAt={300} label="to lock" />
          </div>
          <div className="show-pills">
            <span className="show-pill">
              P1{' '}
              <Badge tone="correct" variant="outline">
                AC
              </Badge>
            </span>
            <span className="show-pill is-on">
              P2{' '}
              <Badge tone="partial" variant="outline">
                6/10
              </Badge>
            </span>
            <span className="show-pill">
              P3{' '}
              <Badge tone="neutral" variant="outline">
                —
              </Badge>
            </span>
          </div>
          <FeedbackBanner
            state="warning"
            title="28 min on a ruled-out approach."
            slug="constraints read at 1:30"
            style={{ marginTop: 12 }}
          >
            The limits ruled out O(N²) early, but you built it anyway and only reframed after the
            first TLE.
          </FeedbackBanner>
        </Frame>

        <Frame label="Mastery · topic heatmap">
          <div className="t-eyebrow" style={{ marginBottom: 10 }}>
            30 topics · red dot = open misconception
          </div>
          <MasteryHeatmap cells={HEATMAP} columns={10} cellSize={20} />
        </Frame>

        <Frame label="Diagnostic · reasoning profile">
          <div className="show-skills">
            {[
              ['Entity extraction', 82],
              ['Constraint reading', 46],
              ['Pattern selection', 61],
              ['Anti-pattern', 34],
              ['State prediction', 70],
            ].map(([n, v]) => (
              <div className="show-skill" key={n as string}>
                <span className="show-skill__n">{n}</span>
                <span className="show-skill__bar">
                  <span
                    style={{
                      width: `${v}%`,
                      background:
                        (v as number) < 50
                          ? 'var(--incorrect)'
                          : (v as number) < 68
                            ? 'var(--partial)'
                            : 'var(--correct)',
                    }}
                  />
                </span>
                <span className="show-skill__v">{v}</span>
              </div>
            ))}
          </div>
        </Frame>
      </div>
    </section>
  );
}
